/**
 * OverlayPropertiesPanel - Property editor for selected overlays
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Displays and edits overlay metadata, coordinates, and publication status.
 * Features:
 * - Scrollable panel with sticky header and footer
 * - Only valid status transitions are enabled
 * - Shows geometry edit lock warning for published overlays
 * - Fetches toggle options from API (not hard-coded)
 */

import React, { useState, useEffect } from 'react';
import { 
  OVERLAY_CATEGORIES, 
  EMPTY_TOGGLE_OPTION,
  STATUS_COLORS,
  PUBLICATION_STATUS,
  getValidNextStatuses,
  getStatusLabel,
  canRepublishWithoutReview
} from '../../constants/mapEditor';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const OverlayPropertiesPanel = ({ 
  overlay, 
  onUpdate, 
  onDelete,
  onStatusChange,
  onClose,
  canEditGeometry = true,
  canEditMetadata = true
}) => {
  const [name, setName] = useState(overlay?.name || '');
  const [category, setCategory] = useState(overlay?.category || 'section');
  const [linkedToggle, setLinkedToggle] = useState(overlay?.linkedToggle || '');
  const [notes, setNotes] = useState(overlay?.notes || '');
  const [isLocked, setIsLocked] = useState(overlay?.isLocked || false);
  
  // Fetch toggle options from API
  const [toggleOptions, setToggleOptions] = useState([EMPTY_TOGGLE_OPTION]);
  const [loadingToggles, setLoadingToggles] = useState(true);
  
  // Fetch toggles from API on mount
  useEffect(() => {
    const fetchToggles = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/public-toggles`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          const apiToggles = (data.toggles || []).map(t => ({
            value: t.id,
            label: t.label
          }));
          setToggleOptions([EMPTY_TOGGLE_OPTION, ...apiToggles]);
        }
      } catch (err) {
        console.error('Failed to fetch toggle options:', err);
      } finally {
        setLoadingToggles(false);
      }
    };
    fetchToggles();
  }, []);
  
  // Sync with overlay changes
  useEffect(() => {
    if (overlay) {
      setName(overlay.name || '');
      setCategory(overlay.category || 'section');
      setLinkedToggle(overlay.linkedToggle || '');
      setNotes(overlay.notes || '');
      setIsLocked(overlay.isLocked || false);
    }
  }, [overlay?.id]);
  
  if (!overlay) return null;
  
  const status = overlay.publication_status || PUBLICATION_STATUS.DRAFT;
  const statusColor = STATUS_COLORS[status] || '#888';
  const hasUnreviewedChanges = overlay.has_unreviewed_changes || false;
  
  // Get valid transitions, accounting for revision status
  const getEffectiveValidStatuses = () => {
    const basicValid = getValidNextStatuses(status);
    
    // If unpublished with unreviewed changes, remove 'published' from valid options
    if (status === PUBLICATION_STATUS.UNPUBLISHED && !canRepublishWithoutReview(overlay)) {
      return basicValid.filter(s => s !== PUBLICATION_STATUS.PUBLISHED);
    }
    
    return basicValid;
  };
  
  const validNextStatuses = getEffectiveValidStatuses();
  
  const handleSave = () => {
    onUpdate({ 
      ...overlay, 
      name, 
      category, 
      linkedToggle, 
      notes,
      isLocked 
    });
  };
  
  const formatCoordinates = () => {
    if (overlay.type === 'rect') {
      return `x:${Math.round(overlay.x)} y:${Math.round(overlay.y)} w:${Math.round(overlay.width)} h:${Math.round(overlay.height)}`;
    }
    if (overlay.type === 'polygon') {
      return `${overlay.points?.length || 0} vertices`;
    }
    if (overlay.type === 'marker') {
      return `x:${Math.round(overlay.x)} y:${Math.round(overlay.y)}`;
    }
    return 'Unknown';
  };
  
  const handleStatusButtonClick = (newStatus) => {
    if (status === newStatus) return;
    if (!validNextStatuses.includes(newStatus)) return;
    onStatusChange(overlay.id, newStatus);
  };
  
  const isStatusButtonDisabled = (targetStatus) => {
    if (status === targetStatus) return true; // Current status
    if (!validNextStatuses.includes(targetStatus)) return true; // Invalid transition
    return false;
  };
  
  const getStatusButtonStyle = (targetStatus) => {
    const isDisabled = isStatusButtonDisabled(targetStatus);
    const isCurrent = status === targetStatus;
    const isValid = validNextStatuses.includes(targetStatus);
    
    return {
      padding: '4px 8px',
      background: isCurrent ? STATUS_COLORS[targetStatus] : 'white',
      color: isCurrent ? 'white' : STATUS_COLORS[targetStatus],
      border: `1px solid ${STATUS_COLORS[targetStatus]}`,
      borderRadius: '4px',
      fontSize: '10px',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? (isCurrent ? 1 : 0.4) : (isValid ? 1 : 0.5),
      transition: 'all 0.15s ease'
    };
  };
  
  const getStatusTooltip = (targetStatus) => {
    if (status === targetStatus) return 'Current status';
    if (!getValidNextStatuses(status).includes(targetStatus)) {
      return `Cannot transition from ${getStatusLabel(status)} to ${getStatusLabel(targetStatus)}`;
    }
    if (targetStatus === PUBLICATION_STATUS.PUBLISHED && status === PUBLICATION_STATUS.UNPUBLISHED && !canRepublishWithoutReview(overlay)) {
      return 'Has unreviewed changes - go through owner_review → approved first';
    }
    return `Transition to ${getStatusLabel(targetStatus)}`;
  };
  
  return (
    <div 
      style={{
        position: 'absolute',
        top: '80px',
        right: '20px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        zIndex: 500,
        width: '280px',
        maxHeight: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      data-testid="overlay-properties-panel"
    >
      {/* Sticky Header */}
      <div style={{ 
        padding: '16px 20px 12px',
        borderBottom: '1px solid #eee',
        background: 'white',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>Properties</div>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', padding: '4px' }}
            data-testid="close-properties-panel"
          >
            ×
          </button>
        </div>
        
        {/* Status Badge */}
        <div>
          <div 
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              background: statusColor + '20',
              color: statusColor,
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}
            data-testid="overlay-status-badge"
          >
            {getStatusLabel(status)}
          </div>
          {isLocked && (
            <span style={{ marginLeft: '8px', color: '#666', fontSize: '11px' }}>
              🔒 Locked
            </span>
          )}
          {hasUnreviewedChanges && (
            <span style={{ marginLeft: '8px', color: '#E91E63', fontSize: '11px' }}>
              ⚠️ Unreviewed
            </span>
          )}
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div style={{ 
        padding: '16px 20px',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        flex: 1
      }}>
        {/* Name */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Section L"
            disabled={!canEditMetadata}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '6px', 
              fontSize: '14px', 
              boxSizing: 'border-box',
              background: canEditMetadata ? 'white' : '#f5f5f5'
            }}
            data-testid="overlay-name-input"
          />
        </div>
        
        {/* Geometry Type (read-only) */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Geometry
          </label>
          <div style={{ 
            padding: '8px 10px', 
            background: '#f5f5f5', 
            borderRadius: '6px', 
            fontSize: '13px',
            textTransform: 'capitalize'
          }}>
            {overlay.type === 'rect' ? 'Rectangle' : overlay.type}
          </div>
        </div>
        
        {/* Category */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Primary Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={!canEditMetadata}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '6px', 
              fontSize: '14px', 
              background: canEditMetadata ? 'white' : '#f5f5f5'
            }}
            data-testid="overlay-category-select"
          >
            {OVERLAY_CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
        
        {/* Toggle Link (from API) */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Link to Public Toggle
          </label>
          <select
            value={linkedToggle}
            onChange={(e) => setLinkedToggle(e.target.value)}
            disabled={!canEditMetadata || loadingToggles}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '6px', 
              fontSize: '14px', 
              background: canEditMetadata ? 'white' : '#f5f5f5'
            }}
            data-testid="overlay-toggle-select"
          >
            {toggleOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {toggleOptions.length === 1 && !loadingToggles && (
            <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>
              No toggles created yet. Create them in Settings.
            </div>
          )}
        </div>
        
        {/* Coordinates (read-only) */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Coordinates (px)
          </label>
          <div style={{ 
            padding: '8px', 
            background: '#f5f5f5', 
            borderRadius: '6px', 
            fontSize: '11px', 
            fontFamily: 'monospace' 
          }}>
            {formatCoordinates()}
            {!canEditGeometry && (
              <div style={{ color: '#d44', marginTop: '4px', fontFamily: 'system-ui', fontSize: '11px' }}>
                ⚠️ {status === PUBLICATION_STATUS.PUBLISHED ? 'Unpublish to edit geometry' : 'Unlock to edit geometry'}
              </div>
            )}
          </div>
        </div>
        
        {/* Lock Toggle */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditMetadata ? 'pointer' : 'default' }}>
            <input
              type="checkbox"
              checked={isLocked}
              onChange={(e) => setIsLocked(e.target.checked)}
              disabled={!canEditMetadata}
              data-testid="overlay-lock-checkbox"
            />
            <span style={{ fontSize: '13px' }}>Lock overlay (prevent edits)</span>
          </label>
        </div>
        
        {/* Notes */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '4px' }}>
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canEditMetadata}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '6px', 
              fontSize: '13px', 
              minHeight: '50px', 
              resize: 'vertical', 
              boxSizing: 'border-box',
              background: canEditMetadata ? 'white' : '#f5f5f5'
            }}
            data-testid="overlay-notes-textarea"
          />
        </div>
        
        {/* Status Workflow */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '0.05em' }}>
            PUBLICATION WORKFLOW
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {Object.values(PUBLICATION_STATUS).map(s => (
              <button
                key={s}
                onClick={() => handleStatusButtonClick(s)}
                disabled={isStatusButtonDisabled(s)}
                title={getStatusTooltip(s)}
                style={getStatusButtonStyle(s)}
                data-testid={`status-btn-${s}`}
              >
                {getStatusLabel(s)}
              </button>
            ))}
          </div>
          {validNextStatuses.length > 0 && (
            <div style={{ fontSize: '10px', color: '#888', marginTop: '6px' }}>
              Valid transitions: {validNextStatuses.map(s => getStatusLabel(s)).join(', ')}
            </div>
          )}
        </div>
      </div>
      
      {/* Sticky Footer */}
      <div style={{ 
        padding: '12px 20px 16px',
        borderTop: '1px solid #eee',
        background: 'white',
        flexShrink: 0
      }}>
        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!canEditMetadata}
          style={{ 
            width: '100%', 
            padding: '12px', 
            background: canEditMetadata ? '#C9A962' : '#ccc', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px', 
            fontSize: '14px', 
            fontWeight: 600, 
            cursor: canEditMetadata ? 'pointer' : 'not-allowed', 
            marginBottom: '8px' 
          }}
          data-testid="save-overlay-btn"
        >
          Save Changes
        </button>
        
        {/* Delete */}
        <button
          onClick={() => onDelete(overlay.id)}
          style={{ 
            width: '100%', 
            padding: '10px', 
            background: 'white', 
            color: '#d44', 
            border: '1px solid #d44', 
            borderRadius: '6px', 
            fontSize: '13px', 
            cursor: 'pointer' 
          }}
          data-testid="delete-overlay-btn"
        >
          Delete Overlay
        </button>
      </div>
    </div>
  );
};

export default OverlayPropertiesPanel;
